import { useNode } from '@craftjs/core';
import { VideoIcon } from 'lucide-react';
import { cn } from '../../../../lib/utils';

interface VideoBlockProps {
  url: string;
  caption: string;
  videoSource: 'url' | 'upload';
}

function getEmbedUrl(url: string): string | null {
  if (!url) return null;

  // YouTube patterns
  const ytShort = url.match(/youtu\.be\/([^?&]+)/);
  if (ytShort) return `https://www.youtube.com/embed/${ytShort[1]}`;

  const ytFull = url.match(/youtube\.com\/watch\?v=([^&]+)/);
  if (ytFull) return `https://www.youtube.com/embed/${ytFull[1]}`;

  const ytEmbed = url.match(/youtube\.com\/embed\/([^?&]+)/);
  if (ytEmbed) return url;

  // Vimeo patterns
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;

  const vimeoEmbed = url.match(/player\.vimeo\.com\/video\/(\d+)/);
  if (vimeoEmbed) return url;

  return null;
}

export function VideoBlock({ url, caption, videoSource }: VideoBlockProps) {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const isUploadedVideo = videoSource === 'upload' && url;
  const embedUrl = !isUploadedVideo ? getEmbedUrl(url) : null;

  return (
    <figure
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      className={cn(
        'block p-1 rounded transition-all',
        selected && 'ring-2 ring-primary ring-offset-2'
      )}
    >
      {isUploadedVideo ? (
        <div className="relative w-full rounded overflow-hidden bg-black">
          <video
            src={url}
            controls
            className="w-full h-auto max-h-[400px]"
            preload="metadata"
          />
        </div>
      ) : embedUrl ? (
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <iframe
            src={embedUrl}
            title={caption || 'Vídeo incorporado'}
            className="absolute inset-0 w-full h-full rounded"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <div className="w-full h-48 flex flex-col items-center justify-center bg-muted rounded text-muted-foreground gap-2">
          <VideoIcon className="h-10 w-10" />
          <span className="text-sm">
            {url
              ? 'URL de vídeo inválida (YouTube ou Vimeo suportados)'
              : 'Configure a URL do vídeo nas propriedades'}
          </span>
        </div>
      )}
      {caption && (
        <figcaption className="text-center text-xs text-muted-foreground mt-2 italic">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

VideoBlock.craft = {
  displayName: 'Vídeo',
  props: {
    url: '',
    caption: '',
    videoSource: 'url' as const,
  },
  rules: { canDrag: () => true },
};
